import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
  Button,
  Card,
  CardContent,
  EqualHeight,
  Grid,
  GridColumn,
  Icon,
  InternetSpeed,
  ITagProps,
  List,
  ListItem,
  Separator,
  Text,
  Tooltip,
  TooltipToggle
} from 'telia-front-react';
import ErrorUtil from '../../../error/ErrorUtil';
import { Segment } from '../../../stores/SegmentStore';
import { renderAddedVat, stripTrailingZeroes } from '../../../util/PriceUtil';
import { twebTranslateOrEmpty } from '../../../util/TranslateUtil';
import { ProductCard } from '../InternetCalculatorInterfaces';
import { isEqualUploadAndDownloadSpeed, getInternetTech } from '../InternetSpeedUtil';
import InternetCalculatorSubmitButton from './InternetCalculatorSubmitButton';

export interface InternetCardProps {
  calculatorKey: string;
  card: ProductCard;
  isRecommended?: boolean;
  segment?: Segment;
  noEmptySpaceOnTop?: boolean;
}

const TURVANET_CATEGORY_CODE = 'TURVANETI_KIIRUSED';
const SEADMENET_CATEGORY_CODE = 'TURVALINE_SEADMENET_KIIRUSED';

const getCardType = (product: ProductCard): 'employee' | null => {
  return product.liableOnlyForSalesConsultant ? 'employee' : null;
};

const InternetCard = (props: InternetCardProps): JSX.Element => {
  const { card, segment, calculatorKey } = props;
  const { translate, translateOrEmpty } = React.useContext(TranslationsContext);
  const offerName = card.productOfferingCode.replace('/', '__');
  const isTurvanet = card.categoryCodes.includes(TURVANET_CATEGORY_CODE);
  const isSeadmenet = card.categoryCodes.includes(SEADMENET_CATEGORY_CODE);
  const categoryLabel = isTurvanet ? 'turvanet' : 'seadmenet';
  const isEqualSpeeds: boolean = isEqualUploadAndDownloadSpeed(card);
  const hasDiscount = !!card.discountPrice?.amount;
  const discountPrice = hasDiscount && +card.price + card.discountPrice.amount;
  const hasDiscountLabel = hasDiscount && !!twebTranslateOrEmpty(translateOrEmpty, `internetCalculator.discount.${card.discountPrice.info}`);
  if (hasDiscount && !hasDiscountLabel) {
    ErrorUtil.pushErrorToTeliaRumAndLog(new Error(`Missing discount label for price: ${card.discountPrice.info}`));
  }

  const getInternetFeatureListItem = (icon: string, text: string) => {
    return (
      <ListItem icon={icon}>
        <p className="text-small text-left text__margin-none">{text}</p>
      </ListItem>
    );
  };

  const getCardTag = (): ITagProps => {
    if (props.isRecommended) {
      return {
        text: translate('internetAndTvCalculator.recommendedLabel'),
        size: 'small',
        purpose: 'campaign-4',
        fontSize: 'tiny'
      };
    }
    return undefined;
  };

  const getInternetTechnology = (): JSX.Element => {
    if (segment === 'BUSINESS') {
      return null;
    }

    const tech = getInternetTech(card.name, card.fix4g);

    return (
      <>
        <Grid horizontalGutter="small" align={['middle-xs']} noWrap={true}>
          <GridColumn width={['min']}>
            <Icon modifiers={tech === 'CABLE' ? '#fiber' : '#wireless'} size="sm" />
          </GridColumn>
          <GridColumn width={['max']}>
            {tech === 'CABLE' ? translate('internetCard.cableInternet') : `${tech} ${translate('internetCard.wirelessInternet')}`}
          </GridColumn>
        </Grid>
        <Separator color="invisible" spacing="xxsmall" />
      </>
    );
  };

  return (
    <Card key={card.productOfferingCode} type={getCardType(card)} tag={getCardTag()} background="brand-secondary-bg-subtle" className="text-left" elevation={1}>
      <CardContent
        tagTop={
          hasDiscount
            ? {
                purpose: 'campaign-3',
                text: translate('internetAndTvCalculator.campaign'),
                size: 'tiny'
              }
            : props.noEmptySpaceOnTop
              ? null
              : true
        }
      >
        <Text>
          <EqualHeight group="internet-speed">
            <p className="text-small">{translate('internetAndTvCalculator.speedUpTo')}</p>
            <Separator color="invisible" spacing="xxsmall" />
            <InternetSpeed
              downSpeed={translate(`internetAndTvCalculator.${offerName}.downSpeed`)}
              downUnit={{
                unit: translate(`internetAndTvCalculator.${offerName}.downUnit`),
                direction: translate('internetAndTvCalculator.direction.down')
              }}
              upSpeed={translate(`internetAndTvCalculator.${offerName}.upSpeed`)}
              upUnit={{
                unit: translate(`internetAndTvCalculator.${offerName}.upUnit`),
                direction: translate('internetAndTvCalculator.direction.up')
              }}
              align={'left'}
              collapsed={isEqualSpeeds}
              multiline={!isEqualSpeeds}
            />
          </EqualHeight>
          <EqualHeight group="card-text">
            {getInternetTechnology()}
            <p>{translate(`internetAndTvCalculator.${offerName}.description`)}</p>
          </EqualHeight>
          <EqualHeight group="price">
            {hasDiscountLabel && (
              <>
                <p className="h4 text-bold">{translate(`internetCalculator.discount.${card.discountPrice.info}`, { discountPrice })}</p>
                {twebTranslateOrEmpty(translateOrEmpty, `internetCalculator.discount.info.${card.discountPrice.info}`) ? (
                  <Tooltip
                    referenceElement={
                      <TooltipToggle id={`tooltip-toggle-price-${card.uid}`}>
                        {translate('internetCalculator.regularPrice')}
                        <strong>
                          {stripTrailingZeroes(card.price)}&nbsp;
                          {translate('internetAndTvCalculator.priceLabel')}
                          {segment === 'BUSINESS' && ` ${renderAddedVat(translate)}`}
                        </strong>
                      </TooltipToggle>
                    }
                    placement="top"
                  >
                    {translate(`internetCalculator.discount.info.${card.discountPrice.info}`)}
                  </Tooltip>
                ) : (
                  <>
                    {translate('internetCalculator.regularPrice')}
                    <strong>
                      {stripTrailingZeroes(card.price)}&nbsp;
                      {translate('internetAndTvCalculator.priceLabel')}
                      {segment === 'BUSINESS' && ` ${renderAddedVat(translate)}`}
                    </strong>
                  </>
                )}
                <Separator color="invisible" spacing="xsmall" />
                {!!twebTranslateOrEmpty(translateOrEmpty, `internetCalculator.discount.terms.${card.discountPrice.info}`) && (
                  <Button
                    url={translate(`internetCalculator.discount.terms.${card.discountPrice.info}`)}
                    text={translate('internetAndTvCalculator.campaign.terms')}
                    link={true}
                    target={'_blank'}
                    iconLeft="#file-pdf"
                    iconLeftSize="xxs"
                    iconClass="text-color-normal"
                  />
                )}
              </>
            )}
            {!hasDiscountLabel && (
              <p className="h4 text-bold">
                <>{card.price.replace('.00', '')} </>
                {translate('internetAndTvCalculator.priceLabel')}
                {segment === 'BUSINESS' && ` ${renderAddedVat(translate)}`}
              </p>
            )}
          </EqualHeight>
        </Text>
        <Separator color="invisible" spacing="small" weight="thin" />
        <InternetCalculatorSubmitButton calculatorKey={calculatorKey} card={card} segment={segment} />
        {(isSeadmenet || isTurvanet) && (
          <>
            <Separator spacing="small" weight="thin" />
            <List type="icon" iconPosition="middle" size="large" className="text-left">
              {getInternetFeatureListItem(isTurvanet ? '#security' : '#terminal', translate(`internetOptionCard.${categoryLabel}.firstListItem`))}
              {getInternetFeatureListItem(isTurvanet ? '#time-round' : '#time-quarter', translate(`internetOptionCard.${categoryLabel}.inCaseOfFailures`))}
              {getInternetFeatureListItem('#ip', translate(`internetOptionCard.${categoryLabel}.ip`))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default observer(InternetCard);
